import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import { VpnKey, CheckCircle, Info } from '@mui/icons-material';
import tokens from '../../theme/tokens';

const CodeInputCard = ({ 
  onCodeSubmit, 
  isProcessing = false, 
  error = null,
  translations 
}) => {
  const [code, setCode] = useState('');
  const [isValid, setIsValid] = useState(false);

  const handleCodeChange = (event) => {
    const value = event.target.value.trim();
    setCode(value);
    // JWT validation - tokens start with 'eyJ' and contain dots
    setIsValid(value.length >= 20 && value.startsWith('eyJ') && value.includes('.'));
  };

  const handleSubmit = () => {
    if (isValid && !isProcessing) {
      onCodeSubmit(code);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 480 }, mx: 'auto' }}>
      <Card
        sx={{
          border: `2px solid ${tokens.colors.borders.light}`,
          borderRadius: { xs: tokens.radius.lg, sm: tokens.radius.xl },
          backgroundColor: tokens.colors.background.paper,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: tokens.shadows.soft,
          '&:hover': {
            borderColor: tokens.colors.secondary.main,
            transform: { xs: 'none', sm: 'translateY(-2px)' },
            boxShadow: tokens.shadows.lg
          }
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <Box
              sx={{
                width: { xs: 56, sm: 64 },
                height: { xs: 56, sm: 64 },
                borderRadius: '50%',
                backgroundColor: `${tokens.colors.secondary.main}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: { xs: 1.5, sm: 2 }
              }}
            >
              <VpnKey sx={{ 
                color: tokens.colors.secondary.main, 
                fontSize: { xs: 24, sm: 28 } 
              }} />
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.bold,
                color: tokens.colors.text.primary,
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                mb: { xs: 0.75, sm: 1 }
              }}
            >
              {translations('payment.codeInput.title')}
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: tokens.colors.text.secondary,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                lineHeight: 1.5,
                maxWidth: { xs: 280, sm: 320 },
                mx: 'auto'
              }}
            >
              {translations('payment.codeInput.description')}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Fade in>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: tokens.radius.base,
                  fontSize: '0.9rem'
                }}
                onClose={() => {/* Error will be cleared by parent */}}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {/* Input Section */}
          <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
            <TextField
              fullWidth
              value={code}
              onChange={handleCodeChange}
              onKeyPress={handleKeyPress}
              placeholder={translations('payment.codeInput.placeholder')}
              disabled={isProcessing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey sx={{ 
                      color: tokens.colors.text.secondary, 
                      fontSize: { xs: 18, sm: 20 } 
                    }} />
                  </InputAdornment>
                ),
                endAdornment: isValid && (
                  <InputAdornment position="end">
                    <CheckCircle sx={{ 
                      color: tokens.colors.success, 
                      fontSize: { xs: 18, sm: 20 } 
                    }} />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '& .MuiInputBase-input': {
                    py: { xs: 1.5, sm: 2 },
                    fontFamily: tokens.typography.fontFamily.mono,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: tokens.radius.base, sm: tokens.radius.lg },
                  backgroundColor: tokens.colors.background.alt,
                  transition: 'all 0.2s ease',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: tokens.colors.secondary.main
                  },
                  '&.Mui-focused': {
                    backgroundColor: tokens.colors.background.paper,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: tokens.colors.secondary.main,
                      borderWidth: 2
                    }
                  }
                }
              }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid || isProcessing}
            sx={{
              backgroundColor: tokens.colors.secondary.main,
              color: 'white',
              textTransform: 'none',
              fontWeight: tokens.typography.fontWeights.semibold,
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              borderRadius: { xs: tokens.radius.base, sm: tokens.radius.lg },
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: tokens.colors.secondary.dark,
                transform: { xs: 'none', sm: 'translateY(-1px)' },
                boxShadow: { xs: 'none', sm: `0 4px 12px ${tokens.colors.secondary.main}40` }
              },
              '&:disabled': {
                backgroundColor: tokens.colors.text.disabled,
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isProcessing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={{ xs: 18, sm: 20 }} sx={{ color: 'white' }} />
                {translations('payment.codeInput.verifying')}
              </Box>
            ) : (
              translations('payment.codeInput.submit')
            )}
          </Button>

          {/* Help Text */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.75, sm: 1 },
              mt: { xs: 2.5, sm: 3 },
              p: { xs: 1.5, sm: 2 },
              backgroundColor: `${tokens.colors.info}08`,
              borderRadius: tokens.radius.base,
              border: `1px solid ${tokens.colors.info}20`
            }}
          >
            <Info sx={{ 
              color: tokens.colors.info, 
              fontSize: { xs: 14, sm: 16 },
              flexShrink: 0
            }} />
            <Typography
              variant="caption"
              sx={{
                color: tokens.colors.text.secondary,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                textAlign: 'center',
                lineHeight: 1.4
              }}
            >
              {translations('payment.codeInput.hint')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CodeInputCard; 